import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';

const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '20px 24px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const inputStyle = { width: '100%', boxSizing: 'border-box', background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 9, padding: '10px 13px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none' };
const labelStyle = { display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 };

export default function SecuritySettings({ onNavigate }) {
  // ── Range Reports Password ───────────────────────────────────────────────────
  const [storedPw, setStoredPw] = useState('');
  const [editing, setEditing] = useState(false);

  const [currentInput, setCurrentInput] = useState('');
  const [newInput, setNewInput] = useState('');
  const [confirmInput, setConfirmInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const pwRes = await supabase.from('app_settings').select('value').eq('key', 'range_reports_password').single();
      if (pwRes.data) setStoredPw(pwRes.data.value);
    })();
  }, []);

  const startEdit = () => {
    setEditing(true);
    setCurrentInput('');
    setNewInput('');
    setConfirmInput('');
    setError('');
    setSuccess('');
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
  };

  const handleSave = async () => {
    setError('');
    if (currentInput !== storedPw) { setError('Current password is incorrect'); return; }
    if (!newInput.trim()) { setError('New password cannot be empty'); return; }
    if (newInput !== confirmInput) { setError('New passwords do not match'); return; }
    setSaving(true);
    const { error: err } = await supabase
      .from('app_settings')
      .update({ value: newInput, updated_at: new Date().toISOString() })
      .eq('key', 'range_reports_password');
    setSaving(false);
    if (err) { setError('Save failed: ' + err.message); return; }
    setStoredPw(newInput);
    setEditing(false);
    setSuccess('Password updated successfully.');
    setTimeout(() => setSuccess(''), 4000);
  };

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
      </div>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Security</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 24 }}>
        Manage access passwords for protected features.
      </p>

      {/* Range Reports Password */}
      <div style={card}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: NAVY, marginBottom: 16 }}>Range Reports Password</div>

        {!editing ? (
          <div>
            <label style={labelStyle}>Current Password</label>
            <div style={{ maxWidth: 320 }}>
              <input
                type="password"
                value={storedPw}
                readOnly
                autoComplete="off"
                style={{ ...inputStyle, color: 'var(--color-ink-secondary)', background: 'var(--color-sunken)', cursor: 'default' }}
              />
            </div>
            <button
              onClick={startEdit}
              style={{ marginTop: 14, padding: '9px 22px', background: NAVY, color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
            >
              Change Password
            </button>
            {success && (
              <div style={{ marginTop: 12, fontSize: '0.82rem', color: '#166534', fontWeight: 600 }}>{success}</div>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: 360 }}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Current Password</label>
              <input
                type="password"
                value={currentInput}
                onChange={e => setCurrentInput(e.target.value)}
                autoFocus
                autoComplete="current-password"
                style={inputStyle}
                placeholder="Enter current password"
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={newInput}
                onChange={e => setNewInput(e.target.value)}
                autoComplete="new-password"
                style={inputStyle}
                placeholder="Enter new password"
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type="password"
                value={confirmInput}
                onChange={e => setConfirmInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !saving && handleSave()}
                autoComplete="new-password"
                style={inputStyle}
                placeholder="Confirm new password"
              />
            </div>
            {error && <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '9px 22px', background: saving ? '#ccc' : NAVY, color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save Password'}
              </button>
              <button
                onClick={cancelEdit}
                style={{ padding: '9px 22px', background: '#fff', color: 'var(--color-ink-secondary)', border: '1.5px solid var(--color-border-strong)', borderRadius: 9, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
